<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template><div class="k-main-collection k-home-home-wrapper">
	<div v-if="!site_config.use_vx_nav" class="d-flex align-center">
		<k-icon color="primary" class="k-collection-header-icon mr-3">{{home_icon}}</k-icon>
		<h1 class="k-page-title mb-2">
			<b v-if="!subdomain_data" v-html="page_title"></b>
			<b v-if="subdomain_data" class="pink--text text--darken-4 ml-1">{{site_config.app_name}} for {{subdomain_data.agency_name}}</b>
		</h1>
		<k-spacer/>
	</div>

	<div class="k-home-home-wrapper-inner">
		<div v-if="site_config.vx_home_banner_html" v-html="site_config.vx_home_banner_html"></div>

		<div v-if="site_config.use_vx_nav" class="k-main-welcome__headline d-flex align-center mb-4">
			<k-spacer/>
			<div class="k-main-welcome__question k-shadow-text mr-8"><b>{{welcome_msg}}</b></div>
			<div class="k-main-welcome__time-date k-shadow-text" v-html="time_string"></div>
			<k-spacer/>
		</div>
		
		<DailyLessonsBtn v-if="$store.getters.show_daily_lessons" style="margin-top:-12px" class="mb-8 mt-6" />

		<div v-if="site_config.show_favorites_on_home=='yes' && signed_in && favorite_collections.length>0" class="k-home-favorites mb-6">
			<div class="k-favorites-header mt-5">
				<k-icon color="#000">{{favorites_icon}}</k-icon>
				{{favorites_header_text}}:
			</div>

			<div v-if="!lti_chooser && user_info.role == 'parent'">
				<div class="text-center" 
					v-if="child_count == 0"
				>You do not appear to be connected to any students in your school’s student information system.</div>

				<div v-else class="d-flex justify-center mt-3 mb-3">
					<span>Viewing courses for student <b class="ml-1">{{ child_data_showing.first_name }} {{ child_data_showing.last_name }}</b></span>
					<k-menu bottom left><template v-slot:activator="{ on }"><k-btn v-if="child_count > 1" small v-on="on" class="ml-2" color="secondary"><k-icon small class="mr-2">fas fa-arrow-right-arrow-left</k-icon>Switch To Student…</k-btn></template>
						<k-list>
							<k-list-item v-for="(child_data, child_email) in user_info.child_data" :key="child_email" 
								@click="switch_child_data_showing(child_email)"
							>
								<k-list-item-title>{{ child_data.first_name }} {{ child_data.last_name }} ({{ child_email }})</k-list-item-title>
							</k-list-item>
						</k-list>
					</k-menu>
				</div>
			</div>

			<div class="d-flex flex-wrap justify-center mt-2">
				<FavoriteTile v-for="(collection) in favorite_collections" 
					:key="collection.class_sourcedIds ? collection.class_sourcedIds[0] : collection.course_code"
					:course="collection"
					:welcome_section_collection_types="['course', 'repo', 'pd', 'my']"
					:course_showing="''"
					@show_course="show_course"
				></FavoriteTile>
			</div>
		</div>


		<div class="fr-view" v-html="accessible_home_page_content"></div>

		<div v-if="oidc_info" class="mt-3 pt-2 mb-1 fr-view" style="border-top:1px solid #ccc; font-size:16px;" v-html="oidc_info"></div>
		
		<div v-if="!signed_in && site_config.show_banner_signin_btn == 'no' && !backdoor_login_only" class="text-center mt-4 mb-2 fr-view">
			<k-btn color="primary" @click="sign_in">
				Sign In to {{ site_config.app_name }}
				<k-icon small class="ml-2">fas fa-sign-in</k-icon>
			</k-btn>
		</div>
	</div>

</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import DailyLessonsBtn from '../home/DailyLessonsBtn'
import FavoriteTile from '../collectionlist/FavoriteTile'

export default {
	components: { DailyLessonsBtn, FavoriteTile },

	props: {
	},

	data() { return {
		time_string: '',
		time_string_colon: true,
		welcome_msg: 'Welcome!',
	}},

	computed: {
		...mapState(['user_info', 'site_config', 'subdomain_data', 'all_courses']),
		...mapGetters(['signed_in', 'role', 'backdoor_login_only', 'favorite_collections', 'child_count', 'child_data_showing', 'lti_chooser']),

		page_title() { return this.site_config.customized_tabs?.home?.title || 'Home' },
		home_icon() { return this.site_config.customized_tabs?.home?.icon || 'fas fa-home' },
		favorites_icon() { return this.site_config.customized_tabs.favorites?.icon || 'fas fa-bookmark' },
		favorites_header_text() {
			if (this.user_info.role === 'parent') return "My Child’s Courses"
			return 'Your Favorite Collections'
		},

		home_page_variant() {
			// if we're viewing a subdomain, get the subdomain variant (it will default to 'staff')
			if (this.subdomain_data) {
				return `staff_${this.subdomain_data.subdomain}`
			}

			// if we only have one variant, return it
			if (this.site_config.home_page_variants.length == 1) {
				return this.site_config.home_page_variants[0]
			}

			// if not signed in and we have an unsignedin variant, return it
			if (!this.signed_in && this.site_config.home_page_variants.includes('unsignedin')) return 'unsignedin'

			// else try to return varient matching role, but if we don't have a student or parent varient, just return 'staff', which will serve for 'signedin'
			if (this.role == 'student' && this.site_config.home_page_variants.includes('student')) return 'student'
			if (this.role == 'parent' && this.site_config.home_page_variants.includes('parent')) return 'parent'	// in this case you should be viewing FamilyHome...
			return 'staff'
		},

		home_page_content() {
			this.$store.dispatch('get_home_page_content', this.home_page_variant)
			return this.$store.state.home_page_content[this.home_page_variant]
		},

		accessible_home_page_content() {
			return this.add_fallback_alt_text(this.home_page_content)
		},

		oidc_info() {
			let s = ''
			if (this.user_info.first_name) s += sr('<div>You are signed in as $1 $2 ($3)</div>', this.user_info.first_name, this.user_info.last_name, this.user_info.email)
			
			if (this.site_config.show_user_system_data_on_home == 'yes') {
				s += '<ul class="ml-4">'
				if (this.user_info.oidc_data?.system_name) s += sr('<li>School system: $1</li>', this.user_info.oidc_data.system_name)
				if (this.user_info.oidc_data?.school_name) s += sr('<li>School: $1</li>', this.user_info.oidc_data.school_name)
				if (this.user_info.oidc_data?.user_role) s += sr('<li>Role: $1</li>', this.user_info.oidc_data.user_role)
				if (this.user_info.first_name) s += '</ul>'
			}
			return s
		},

		child_email_showing: {
			get() { return this.$store.state.lst.child_email_showing },
			set(val) { this.$store.commit('lst_set', ['child_email_showing', val]) }
		},
	},
	
	created() {
		this.calculate_time_string();

		setInterval(() => {
			this.calculate_time_string();
		}, 1000)
	},
	mounted() {
	},

	methods: {
		calculate_time_string() {
			// this.time_string_colon = !this.time_string_colon
			let d = new Date()

			let hour = d.getHours()

			let time_of_day
			if (hour > 2 && hour < 12) time_of_day = 'morning'
			else if (hour < 18) time_of_day = 'afternoon'
			else time_of_day = 'evening'

			this.welcome_msg = 'Good ' + time_of_day
			if (this.user_info.first_name) this.welcome_msg += ', ' + this.user_info.first_name
			this.welcome_msg += '!'

			hour = hour % 12
			if (hour == 0) hour = 12

			let minute = d.getMinutes()
			if (minute < 10) minute = '0' + minute

			// Thu Jan 29&nbsp;&nbsp;10:50
			this.time_string = sr(
				'It’s&nbsp;$4<span style="$5">:</span>$6&nbsp;on&nbsp;$1, $2 $3',
				['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()],
				['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][d.getMonth()],
				d.getDate(),
				hour,
				this.time_string_colon ? '' : 'visibility:hidden',
				minute,
			)
		},

		add_fallback_alt_text(html) {
			if (!html) return html
			if (typeof DOMParser == 'undefined') {
				return html
					.replace(/<img\b(?![^>]*\balt=)/gi, `<img alt="${this.site_config.app_name} home page image"`)
					.replace(/<h1\b/gi, '<h2')
					.replace(/<\/h1>/gi, '</h2>')
			}

			let parser = new DOMParser()
			let doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
			let wrapper = doc.body.firstElementChild
			if (!wrapper) return html

			for (let heading of wrapper.querySelectorAll('h1')) {
				let replacement = doc.createElement('h2')
				for (let attr of heading.attributes) replacement.setAttribute(attr.name, attr.value)
				replacement.innerHTML = heading.innerHTML
				heading.replaceWith(replacement)
			}

			for (let heading of wrapper.querySelectorAll('h1, h2, h3, h4, h5, h6')) {
				let has_text = heading.textContent && heading.textContent.trim().length > 0
				let has_meaningful_media = heading.querySelector('img, svg, picture, video')
				if (!has_text && !has_meaningful_media) heading.remove()
			}

			let img_count = 0
			for (let img of wrapper.querySelectorAll('img')) {
				if (img.hasAttribute('alt')) continue
				img_count += 1
				img.setAttribute('alt', img_count == 1 ? `${this.site_config.app_name} branded image` : `${this.site_config.app_name} home page image`)
			}

			return wrapper.innerHTML
		},

		show_course(course, event, unit_id) {
			if (empty(unit_id)) unit_id = 0

			// for legacy reasons the course will not be a proper Learning_Progression object; look up the proper lp
			let lp = this.all_courses.find(x=>x.course_code == course.course_code)

			let path = lp.vue_route(unit_id)
			if (path != this.$router.currentRoute.path) {
				this.$router.push({ path: path })
			}
		},

		switch_child_data_showing(child_email) {
			this.child_email_showing = child_email
		},

		sign_in($evt) {
			// if user holds down shift and cmd (mac, prob. alt on windows), always show native cureum login; this allows, e.g., for us to sign in using the magic password to Inspire
			if ($evt && $evt.shiftKey && $evt.metaKey) {
				vapp.app_mode = 'login'

			} else {
				vapp.redirect_to_login()
			}
		},
	}
}
</script>

<style lang="scss">
.k-home-home-wrapper {
	min-height: 300px;
}

.k-home-home-wrapper-inner {
	padding: 0 16px;
	
	.fr-view {
		font-size: 18px;
		line-height: 1.4em;
		max-width: 920px;
		margin: 0 auto;
		text-align: left;

		h2 {
			font-size: 27px;
			line-height: 33px;
		}
		h3 {
			font-size: 22px;
			line-height: 27px;
		}
	}

	.k-main-welcome__headline {
		display: inline-flex;
		width: auto;
		max-width: max-content;
		margin: 0 auto;
		padding: 8px 16px;
		border-radius: 14px;
		background: rgba(0, 0, 0, 0.62);
		// background: rgb(29, 72, 192, 0.8);
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
		backdrop-filter: blur(2px);
		-webkit-backdrop-filter: blur(2px);
	}

	.k-main-welcome__question {
		font-size: 24px;
		line-height: 30px;
		font-weight: bold;
		color: #fff;
	}

	.k-main-welcome__time-date {
		font-family: $display-font;
		font-weight: 900;
		font-size: 24px;
		color: #fff;
	}

}

.k-main-wrapper .k-home-favorites {
	margin-left:-32px;
	margin-right:-32px;

	.k-favorites-header {
		font-size: 24px;
		font-weight:900;
		text-align:center;

		.v-icon {
			font-size:28px;
			margin-right:8px;
			margin-top:-4px;
		}
	}

	.k-cv-lp-tile {
		width:140px;
		height:140px;
		border-radius:24px;
		text-align:left;
		padding:0;
		margin:8px;
		flex-direction: row;
		align-items: center;
		cursor:pointer;

		.k-cv-lp-tile__title {
			flex:1 1 140px;
			display:block;
			flex-direction: column;
			justify-content: space-around;
			height:140px;
			border-radius:20px;
			font-size:16px;
			line-height:20px;
			font-weight:bold;
			color:#fff;
			padding:36px 8px 8px 8px;

			.v-icon {
				position:absolute;
				left:12px;
				top:12px;
				font-size:20px!important;
			}

			.k-cv-lp-tile__title-teacher {
				font-size:12px;
				line-height:15px;
				font-weight:normal;
			}
		}
	}
	.k-main-collection-item.k-cv-lp-tile.k-lp-tile-long-title .k-cv-lp-tile__title{
		font-size: 13px!important;
		line-height: 16px!important;
	}

	.k-main-collection-item.k-cv-lp-tile.k-lp-tile-extra-long-title .k-cv-lp-tile__title{
		font-size: 11px!important;
		line-height: 14px!important;
	}


}
</style>
